import React, { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  LayoutDashboard,
  FileText,
  Star,
  HelpCircle,
  Briefcase,
  Users,
  ChevronRight,
  ChevronLeft,
  ExternalLink,
  Layers,
  Search,
  Globe,
  Image,
  Layout,
} from "lucide-react";
import useToken from "../../../hooks/features/auth/useToken";

interface SidebarProps {
  isCollapsed: boolean;
  setIsCollapsed: (collapsed: boolean) => void;
}

interface MenuItem {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  route: string;
  badge?: string | number;
  externe?: boolean;
}

interface MenuSection {
  title?: string;
  items: MenuItem[];
}

export const Sidebar: React.FC<SidebarProps> = ({ isCollapsed, setIsCollapsed }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { token } = useToken();
  const [searchQuery, setSearchQuery] = useState("");

  const menuSections: MenuSection[] = [
    {
      items: [
        { icon: LayoutDashboard, label: "Vue d'ensemble", route: "/dashboard" },
      ],
    },
    {
      title: "Gestion de Contenu",
      items: [
        { icon: FileText, label: "Articles & Blog", route: "/blog/posts" },
        { icon: Star, label: "Avis clients", route: "/reviews" },
        { icon: HelpCircle, label: "Foire aux questions", route: "/faq" },
      ],
    },
    {
      title: "CMS - Sites & Pages",
      items: [
        { icon: Globe, label: "Sites", route: "/sites" },
        { icon: FileText, label: "Pages", route: "/pages" },
        { icon: Image, label: "Médias", route: "/media" },
      ],
    },
    {
      title: "CMS - Multilingue",
      items: [
        { icon: Globe, label: "Langues", route: "/locales" },
      ],
    },
    {
      title: "Recrutement & RH",
      items: [
        { icon: Briefcase, label: "Offres d'emploi", route: "/job-offers" },
        { icon: Users, label: "Candidatures", route: "/candidatures" },
      ],
    },
  ];

  const handleNavigation = (route: string, externe?: boolean) => {
    if (externe) {
      window.open(`${route}/redirect?token=${token}`, "_blank");
    } else {
      navigate(`/admin${route}`);
    }
  };

  const isActiveRoute = (route: string) => location.pathname.includes(route);

  const getFilteredSections = () => {
    if (!searchQuery) return menuSections;

    return menuSections
      .map((section) => ({
        ...section,
        items: section.items.filter((item) =>
          item.label.toLowerCase().includes(searchQuery.toLowerCase())
        ),
      }))
      .filter((section) => section.items.length > 0);
  };

  const filteredSections = getFilteredSections();

  return (
    <aside
      className={`relative bg-secondary border-r border-secondary-border transition-all duration-300 ease-in-out ${
        isCollapsed ? "w-20" : "w-72"
      } h-screen flex flex-col justify-between select-none z-20`}
    >
      <button
        onClick={() => setIsCollapsed(!isCollapsed)}
        className="absolute -right-3 top-7 bg-secondary border border-secondary-border rounded-full p-1 text-secondary-text hover:text-secondary-activeText shadow-md transition-all"
        aria-label="Réduire la barre latérale"
      >
        {isCollapsed ? <ChevronRight className="w-3.5 h-3.5" /> : <ChevronLeft className="w-3.5 h-3.5" />}
      </button>

      <div className="flex flex-col">
        <div className="h-16 flex items-center px-4 border-b border-secondary-border gap-3">
          <div className="w-9 h-9 rounded-xl bg-primary flex items-center justify-center shrink-0 shadow-sm">
            <img src="/logo.png" alt="Logo" className="w-5 h-5 object-contain" />
          </div>
          {!isCollapsed && (
            <div className="flex flex-col min-w-0">
              <span className="font-bold text-sm text-secondary-activeText leading-tight truncate">
                Kafintech CMS
              </span>
              <span className="text-[11px] text-secondary-text truncate">Workspace Admin</span>
            </div>
          )}
        </div>

        {!isCollapsed && (
          <div className="p-3 pb-1">
            <div className="relative flex items-center">
              <Search className="w-4 h-4 absolute left-3 text-secondary-text" />
              <input
                type="text"
                placeholder="Filtrer le menu..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full bg-secondary-surface text-xs text-secondary-activeText placeholder:text-secondary-text/60 pl-9 pr-3 py-2 rounded-lg border border-secondary-border focus:outline-none focus:border-primary transition-colors"
              />
            </div>
          </div>
        )}

        <div className="p-3 space-y-6 overflow-y-auto max-h-[calc(100vh-140px)]">
          {filteredSections.map((section, sIdx) => (
            <div key={sIdx} className="space-y-1">
              {section.title && !isCollapsed && (
                <h3 className="px-3 text-[10px] font-bold uppercase tracking-wider text-secondary-text/70 mb-2">
                  {section.title}
                </h3>
              )}

              {section.items.map((item, iIdx) => {
                const Icon = item.icon;
                const active = isActiveRoute(item.route);

                return (
                  <button
                    key={iIdx}
                    onClick={() => handleNavigation(item.route, item.externe)}
                    title={isCollapsed ? item.label : undefined}
                    className={`group relative w-full flex items-center rounded-xl px-3 py-2.5 text-xs font-medium transition-all ${
                      active
                        ? "bg-primary text-white shadow-md font-semibold"
                        : "text-secondary-text hover:bg-secondary-surface hover:text-secondary-activeText"
                    } ${isCollapsed ? "justify-center" : "gap-3"}`}
                  >
                    <Icon
                      className={`w-4 h-4 shrink-0 transition-colors ${
                        active ? "text-white" : "text-secondary-text group-hover:text-secondary-activeText"
                      }`}
                    />

                    {!isCollapsed && (
                      <div className="flex flex-1 items-center justify-between min-w-0">
                        <span className="truncate">{item.label}</span>
                        {item.externe ? (
                          <ExternalLink className="w-3.5 h-3.5 text-secondary-text" />
                        ) : item.badge ? (
                          <span className="text-[10px] px-2 py-0.5 rounded-full bg-secondary-surface text-secondary-activeText font-bold">
                            {item.badge}
                          </span>
                        ) : null}
                      </div>
                    )}
                  </button>
                );
              })}
            </div>
          ))}
        </div>
      </div>

      <div className="p-3 border-t border-secondary-border bg-secondary-surface/40">
        <div className={`flex items-center ${isCollapsed ? "justify-center" : "gap-3 px-2"}`}>
          <div className="w-7 h-7 rounded-lg bg-primary/20 text-primary flex items-center justify-center font-bold text-xs shrink-0">
            <Layers className="w-3.5 h-3.5" />
          </div>
          {!isCollapsed && (
            <div className="flex flex-col min-w-0">
              <span className="text-xs font-semibold text-secondary-activeText truncate">Environnement</span>
              <span className="text-[10px] text-secondary-text truncate">Production v2.4</span>
            </div>
          )}
        </div>
      </div>
    </aside>
  );
};